// src/pages/Chat.js — WhatsApp-style layout
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { T } from '../components/UI';
import { useAuth } from '../context/AuthContext';
import {
  listConversations, getMessages, sendChatMessage,
  markConversationRead, getOrCreateDM, listUsers,
} from '../services/api';

const POLL_MS = 5000;
const WA_GREEN  = '#00d4ff';   // our brand cyan plays the "WhatsApp green" role
const WA_BUBBLE_ME = '#1e88e5';
const WA_BUBBLE_THEM = '#0f2742';
const WA_BG     = '#050b18';
const WA_PANEL  = '#0b1f3a';
const WA_HEADER = '#0d2545';
const WA_BORDER = '#1a3556';

export default function Chat() {
  const { user: me } = useAuth();
  const [convos, setConvos]       = useState([]);
  const [activeId, setActiveId]   = useState(null);
  const [showNewDM, setShowNewDM] = useState(false);
  const [search, setSearch]       = useState('');

  const loadConvos = useCallback(() => {
    listConversations().then(r => {
      setConvos(r.data.data);
      setActiveId(prev => prev ?? (r.data.data.find(c => c.type==='TEAM')?.id ?? r.data.data[0]?.id ?? null));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    loadConvos();
    const iv = setInterval(loadConvos, POLL_MS);
    return () => clearInterval(iv);
  }, [loadConvos]);

  const startDM = async (userId) => {
    const r = await getOrCreateDM(userId);
    setActiveId(r.data.data.id);
    setShowNewDM(false);
    loadConvos();
  };

  const active = convos.find(c => c.id === activeId);
  const filtered = convos.filter(c =>
    !search || c.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ display:'flex', height:'calc(100vh - 80px)', background:WA_BG, borderRadius:'12px', overflow:'hidden', border:`1px solid ${WA_BORDER}` }}>

      {/* ── Left panel ─────────────────────────────── */}
      <div style={{ width:'300px', flexShrink:0, display:'flex', flexDirection:'column', borderRight:`1px solid ${WA_BORDER}`, background:WA_PANEL }}>

        {/* Header */}
        <div style={{ background:WA_HEADER, padding:'14px 16px', display:'flex', justifyContent:'space-between', alignItems:'center', borderBottom:`1px solid ${WA_BORDER}` }}>
          <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
            <div style={avatar(me?.name, 34, WA_GREEN, '#0b1f3a')} />
            <span style={{ fontSize:'14px', fontWeight:700, color:'#fff' }}>{me?.name}</span>
          </div>
          <button title="New direct message" onClick={() => setShowNewDM(true)}
            style={{ background:'none', border:'none', cursor:'pointer', color:WA_GREEN, fontSize:'20px', lineHeight:1, padding:'4px' }}>
            ✏️
          </button>
        </div>

        {/* Search */}
        <div style={{ padding:'8px 12px', background:WA_PANEL, borderBottom:`1px solid ${WA_BORDER}` }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍  Search conversations"
            style={{ width:'100%', background:'rgba(255,255,255,0.07)', border:'none', borderRadius:'20px', padding:'8px 14px', color:'#fff', fontSize:'13px', outline:'none', boxSizing:'border-box' }} />
        </div>

        {/* Conversation list */}
        <div style={{ flex:1, overflowY:'auto' }}>
          {filtered.length === 0 ? (
            <div style={{ padding:'24px', textAlign:'center', color:'rgba(255,255,255,0.3)', fontSize:'13px' }}>No conversations</div>
          ) : filtered.map(c => (
            <ConvoRow key={c.id} c={c} active={c.id === activeId} me={me} onClick={() => setActiveId(c.id)} />
          ))}
        </div>
      </div>

      {/* ── Right panel ────────────────────────────── */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0, position:'relative', backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60'%3E%3Ccircle cx='30' cy='30' r='1' fill='%231a3556' opacity='0.4'/%3E%3C/svg%3E")`, backgroundSize:'60px 60px', background:WA_BG }}>
        {active ? (
          <ConversationView key={active.id} conversation={active} me={me} onRead={loadConvos} />
        ) : (
          <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'14px' }}>
            <div style={{ fontSize:'60px' }}>💬</div>
            <div style={{ fontSize:'20px', fontWeight:700, color:'#fff' }}>Master Solver Chat</div>
            <div style={{ fontSize:'13px', color:'rgba(255,255,255,0.4)', textAlign:'center', maxWidth:'260px' }}>
              Select a conversation or start a new direct message
            </div>
          </div>
        )}
      </div>

      {showNewDM && <NewDMModal onClose={() => setShowNewDM(false)} onSelect={startDM} myId={me?.id} />}
    </div>
  );
}

function ConvoRow({ c, active, me, onClick }) {
  const lastMsg  = c.last_message;
  const preview  = lastMsg ? (lastMsg.sender_id === me?.id ? `You: ${lastMsg.body}` : lastMsg.body) : 'No messages yet';
  const time     = lastMsg ? fmtTime(lastMsg.created_at) : '';
  const isTeam   = c.type === 'TEAM';

  return (
    <div onClick={onClick} style={{
      display:'flex', alignItems:'center', gap:'12px', padding:'12px 14px',
      borderBottom:`1px solid ${WA_BORDER}`, cursor:'pointer',
      background: active ? 'rgba(0,212,255,0.08)' : 'transparent',
      transition:'background .15s',
    }}>
      <div style={{ position:'relative', flexShrink:0 }}>
        {isTeam
          ? <div style={{ width:44, height:44, borderRadius:'50%', background:WA_GREEN, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'20px' }}>👥</div>
          : <div style={avatar(c.name, 44, WA_GREEN, '#0b1f3a')} />
        }
        {/* Online dot — always shown for simplicity (could be real presence later) */}
        <div style={{ position:'absolute', bottom:1, right:1, width:11, height:11, background:'#22c55e', borderRadius:'50%', border:`2px solid ${WA_PANEL}` }} />
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:'3px' }}>
          <span style={{ fontSize:'14px', fontWeight:600, color:'#fff', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.name}</span>
          <span style={{ fontSize:'11px', color: c.unread_count > 0 ? WA_GREEN : 'rgba(255,255,255,0.35)', flexShrink:0, marginLeft:'6px' }}>{time}</span>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontSize:'12px', color:'rgba(255,255,255,0.45)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }}>
            {preview.length > 38 ? preview.slice(0,38)+'…' : preview}
          </span>
          {c.unread_count > 0 && (
            <span style={{ background:WA_GREEN, color:'#0b1f3a', borderRadius:'50%', minWidth:'20px', height:'20px', display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:'11px', fontWeight:800, flexShrink:0, marginLeft:'8px', padding:'0 4px' }}>
              {c.unread_count > 99 ? '99+' : c.unread_count}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function ConversationView({ conversation, me, onRead }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [draft, setDraft]       = useState('');
  const [sending, setSending]   = useState(false);
  const scrollRef  = useRef(null);
  const lastIdRef  = useRef(0);
  const inputRef   = useRef(null);

  const load = useCallback((isPoll) => {
    getMessages(conversation.id, isPoll ? lastIdRef.current : undefined)
      .then(r => {
        const msgs = r.data.data;
        if (msgs.length) {
          lastIdRef.current = msgs[msgs.length-1].id;
          setMessages(prev => isPoll ? [...prev, ...msgs] : msgs);
        } else if (!isPoll) setMessages([]);
      }).catch(() => {}).finally(() => setLoading(false));
  }, [conversation.id]);

  useEffect(() => {
    lastIdRef.current = 0; setMessages([]); setLoading(true);
    load(false);
    markConversationRead(conversation.id).then(onRead).catch(() => {});
    const iv = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(iv);
  }, [conversation.id, load, onRead]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const r = await sendChatMessage(conversation.id, body);
      setMessages(prev => [...prev, r.data.data]);
      lastIdRef.current = r.data.data.id;
      setDraft('');
      onRead();
      setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 50);
    } catch {
    } finally { setSending(false); }
  };

  const isTeam = conversation.type === 'TEAM';

  // Group messages by date for date separators (WhatsApp-style)
  const grouped = groupByDate(messages);

  return (
    <>
      {/* Chat header */}
      <div style={{ background:WA_HEADER, padding:'10px 16px', display:'flex', alignItems:'center', gap:'12px', borderBottom:`1px solid ${WA_BORDER}`, flexShrink:0 }}>
        {isTeam
          ? <div style={{ width:40, height:40, borderRadius:'50%', background:WA_GREEN, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'18px' }}>👥</div>
          : <div style={avatar(conversation.name, 40, WA_GREEN, '#0b1f3a')} />
        }
        <div>
          <div style={{ fontSize:'14px', fontWeight:700, color:'#fff' }}>{conversation.name}</div>
          <div style={{ fontSize:'11px', color:WA_GREEN, marginTop:'1px' }}>
            {isTeam ? 'Team channel — all staff' : (conversation.other_user?.role || 'Direct message')}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} style={{ flex:1, overflowY:'auto', padding:'12px 16px', display:'flex', flexDirection:'column', gap:'2px' }}>
        {loading ? (
          <div style={{ textAlign:'center', color:'rgba(255,255,255,0.3)', paddingTop:'40px' }}>Loading…</div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign:'center', color:'rgba(255,255,255,0.3)', paddingTop:'40px', fontSize:'13px' }}>
            No messages yet — say hello! 👋
          </div>
        ) : grouped.map(({ date, msgs }) => (
          <React.Fragment key={date}>
            <DateSep label={date} />
            {msgs.map((m, i) => {
              const mine    = m.sender_id === me?.id;
              const prevMsg = msgs[i-1];
              const isSameSender = prevMsg && prevMsg.sender_id === m.sender_id;
              return (
                <Bubble key={m.id} message={m} mine={mine} showName={isTeam && !mine && !isSameSender} />
              );
            })}
          </React.Fragment>
        ))}
      </div>

      {/* Input bar */}
      <div style={{ background:WA_HEADER, padding:'10px 14px', display:'flex', gap:'10px', alignItems:'flex-end', borderTop:`1px solid ${WA_BORDER}`, flexShrink:0 }}>
        <textarea ref={inputRef} value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Type a message…"
          rows={1}
          style={{
            flex:1, background:'rgba(255,255,255,0.07)', border:'none', borderRadius:'22px',
            padding:'10px 16px', color:'#fff', fontSize:'14px', outline:'none', resize:'none',
            maxHeight:'120px', fontFamily:'inherit', lineHeight:1.4,
          }}
        />
        <button onClick={send} disabled={!draft.trim() || sending}
          style={{
            width:42, height:42, borderRadius:'50%', background:draft.trim() ? WA_GREEN : 'rgba(255,255,255,0.1)',
            border:'none', cursor: draft.trim() ? 'pointer' : 'not-allowed',
            display:'flex', alignItems:'center', justifyContent:'center', fontSize:'18px',
            flexShrink:0, transition:'background .2s',
          }}>
          {sending ? '⏳' : '➤'}
        </button>
      </div>
    </>
  );
}

function Bubble({ message, mine, showName }) {
  const time = fmtTime(message.created_at);
  return (
    <div style={{ display:'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom:'2px' }}>
      <div style={{
        maxWidth:'65%', padding:'8px 12px 6px',
        borderRadius: mine ? '12px 2px 12px 12px' : '2px 12px 12px 12px',
        background: mine ? WA_BUBBLE_ME : WA_BUBBLE_THEM,
        boxShadow:'0 1px 3px rgba(0,0,0,0.3)',
        position:'relative',
      }}>
        {showName && (
          <div style={{ fontSize:'11px', fontWeight:700, color:WA_GREEN, marginBottom:'3px' }}>{message.sender_name}</div>
        )}
        <div style={{ fontSize:'14px', color:'#fff', lineHeight:1.45, whiteSpace:'pre-wrap', wordBreak:'break-word' }}>
          {message.body}
        </div>
        <div style={{ fontSize:'10px', color:'rgba(255,255,255,0.45)', textAlign:'right', marginTop:'4px', display:'flex', justifyContent:'flex-end', alignItems:'center', gap:'4px' }}>
          {time}
          {mine && <span style={{ color:WA_GREEN, fontSize:'12px' }}>✓✓</span>}
        </div>
      </div>
    </div>
  );
}

function DateSep({ label }) {
  return (
    <div style={{ textAlign:'center', margin:'8px 0' }}>
      <span style={{ background:'rgba(0,0,0,0.45)', color:'rgba(255,255,255,0.6)', borderRadius:'12px', padding:'4px 14px', fontSize:'11px', fontWeight:600 }}>
        {label}
      </span>
    </div>
  );
}

function NewDMModal({ onClose, onSelect, myId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    listUsers().then(r => setUsers(r.data.data.filter(u => u.id !== myId && u.is_active))).finally(() => setLoading(false));
  }, [myId]);

  const filtered = users.filter(u => !search || u.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:300, padding:'20px' }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background:WA_PANEL, border:`1px solid ${WA_BORDER}`, borderRadius:'14px', width:'100%', maxWidth:'340px', overflow:'hidden' }}>
        <div style={{ background:WA_HEADER, padding:'16px', fontWeight:700, color:WA_GREEN, fontSize:'14px', borderBottom:`1px solid ${WA_BORDER}` }}>
          New direct message
        </div>
        <div style={{ padding:'10px 12px', borderBottom:`1px solid ${WA_BORDER}` }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍  Search staff…"
            style={{ width:'100%', background:'rgba(255,255,255,0.07)', border:'none', borderRadius:'20px', padding:'8px 14px', color:'#fff', fontSize:'13px', outline:'none', boxSizing:'border-box' }} />
        </div>
        <div style={{ maxHeight:'300px', overflowY:'auto' }}>
          {loading ? <div style={{ padding:'24px', textAlign:'center', color:'rgba(255,255,255,0.4)' }}>Loading…</div>
          : filtered.length === 0 ? <div style={{ padding:'24px', textAlign:'center', color:'rgba(255,255,255,0.4)', fontSize:'13px' }}>No staff found</div>
          : filtered.map(u => (
            <div key={u.id} onClick={() => onSelect(u.id)}
              style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 16px', cursor:'pointer', borderBottom:`1px solid ${WA_BORDER}`, transition:'background .15s' }}
              onMouseEnter={e => e.currentTarget.style.background='rgba(0,212,255,0.07)'}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}>
              <div style={avatar(u.name, 40, WA_GREEN, '#0b1f3a')} />
              <div>
                <div style={{ fontSize:'14px', fontWeight:600, color:'#fff' }}>{u.name}</div>
                <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.4)' }}>{u.role}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────
function avatar(name, size, bg, color) {
  const initials = (name||'?').replace('Mr. ','').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
  return {
    width: size, height: size, borderRadius:'50%', background: bg,
    display:'flex', alignItems:'center', justifyContent:'center',
    fontSize: size * 0.36, fontWeight:800, color, flexShrink:0, userSelect:'none',
  };
}

function fmtTime(dt) {
  if (!dt) return '';
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) return d.toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' });
  return d.toLocaleDateString('en-KE', { day:'numeric', month:'short' });
}

function groupByDate(messages) {
  const groups = [];
  let currentDate = null;
  let currentGroup = null;
  for (const m of messages) {
    const d = new Date(m.created_at);
    const label = dateLabel(d);
    if (label !== currentDate) {
      currentDate = label;
      currentGroup = { date: label, msgs: [] };
      groups.push(currentGroup);
    }
    currentGroup.msgs.push(m);
  }
  return groups;
}

function dateLabel(d) {
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return 'Today';
  const yesterday = new Date(now); yesterday.setDate(now.getDate()-1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-KE', { weekday:'long', day:'numeric', month:'long' });
}
